import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDetail } from '../../lib/useDetail';
import { money, num } from '../../lib/format';
import type { Dashboard, DashboardMonth, Insights, InsightTone } from '../../lib/types';
import { EmptyState, ErrorNotice, Spinner, YearField } from '../../components/ui';

const TONE_COLOR: Record<InsightTone, string> = {
  ok: 'var(--ok)',
  info: 'var(--info)',
  warn: 'var(--warn)',
  bad: 'var(--bad)',
  neutral: 'var(--ink-3)',
};

function Kpi({ label, value, unit, foot, tone, alert, spark, lead }: {
  label: string;
  value: string;
  unit?: string;
  foot?: React.ReactNode;
  tone?: InsightTone;
  alert?: boolean;
  spark?: number[];
  lead?: boolean;
}) {
  return (
    <div className={`kpi${alert ? ' alert' : ''}${lead ? ' kpi-lead' : ''}`}>
      <div className="k-label">{label}</div>
      <div className="k-value" style={tone ? { color: TONE_COLOR[tone] } : undefined}>
        {value}{unit ? <small>{unit}</small> : null}
      </div>
      {foot ? <div className="k-foot">{foot}</div> : null}
      {spark && spark.length > 1 ? <Spark values={spark} /> : null}
    </div>
  );
}

// 迷你趋势线：来自真实逐月营业序列（净价），非演示数据。末端实心点标记最新月。
function Spark({ values }: { values: number[] }) {
  const W = 120, H = 26, pad = 2;
  const max = Math.max(...values, 1);
  const min = Math.min(...values, 0);
  const span = max - min || 1;
  const step = (W - pad * 2) / (values.length - 1);
  const pts = values.map((v, i) => {
    const x = pad + i * step;
    const y = H - pad - ((v - min) / span) * (H - pad * 2);
    return [x, y] as const;
  });
  const line = pts.map((p) => `${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' ');
  const area = `${pad},${H - pad} ${line} ${(W - pad)},${H - pad}`;
  const last = pts[pts.length - 1];
  return (
    <div className="spark-wrap" role="img" aria-label="逐月营业趋势">
      <svg className="spark" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none">
        <polygon className="spark-area" points={area} />
        <polyline className="spark-line" points={line} />
      </svg>
      <span className="spark-dot" style={{ left: `${(last[0] / W) * 100}%`, top: `${(last[1] / H) * 100}%` }} />
    </div>
  );
}

/* ============ 营业额与产值趋势：营业额柱（含税） + 产值折线（不含税），真实数据 ============ */
/* 几何：SVG viewBox 纵向 1:1 映射像素（H=216），横向 100 单位拉伸铺满；
   文字/圆点/提示框用 HTML 绝对定位叠加，避免非等比缩放造成的字形畸变。 */

const TREND_H = 216;
const TREND_TOP = 12;    // 顶部留白（放柱顶数值）
const TREND_BASE = 202;  // 基线（像素）
const TREND_INNER = TREND_BASE - TREND_TOP;

type TrendMode = 'both' | 'gross' | 'net';

/* 万以下与十万以下千位均保留 1 位小数（12.5k 不失真成 13k）；去尾 .0 */
const fmtShort = (v: number) => {
  if (v >= 1e6) return `${(v / 1e6).toFixed(1).replace(/\.0$/, '')}M`;
  if (v >= 1e3) return `${(v / 1e3).toFixed(1).replace(/\.0$/, '')}k`;
  return `${Math.round(v)}`;
};

function niceMax(v: number): number {
  if (v <= 0) return 1;
  const mag = 10 ** Math.floor(Math.log10(v));
  const n = v / mag;
  const step = n <= 1 ? 1 : n <= 2 ? 2 : n <= 2.5 ? 2.5 : n <= 5 ? 5 : 10;
  return step * mag;
}

function TrendChart({ data, mode }: { data: DashboardMonth[]; mode: TrendMode }) {
  const [hover, setHover] = useState<number | null>(null);
  const rows = Array.isArray(data) ? data : [];
  const n = rows.length;

  const gross = rows.map((m) => Number(m?.total_usd) || 0);
  const net = rows.map((m) => Number(m?.net_usd ?? m?.total_usd) || 0);
  const pool = mode === 'gross' ? gross : mode === 'net' ? net : gross.map((v, i) => Math.max(v, net[i]));
  const maxV = Math.max(...pool, 0);
  if (!n || maxV <= 0) {
    return <div className="muted" style={{ fontSize: 12.5, padding: '18px 4px' }}>该范围内暂无营业月份数据。</div>;
  }

  const top = niceMax(maxV);
  const yPx = (v: number) => TREND_BASE - (v / top) * TREND_INNER;
  const slot = 100 / n;
  const gap = n > 8 ? 0.3 : 0.38;
  // 单月/少月时按“虚拟 6 槽”封顶柱宽，避免一根柱占满图区
  const bw = Math.min(slot * (1 - gap), (100 / Math.max(n, 6)) * 0.62);
  const ticks = [0.25, 0.5, 0.75, 1].map((t) => top * t);
  const every = Math.ceil(n / 14); // x 轴标签抽样，避免过密
  const showBarLabels = n <= 12;
  const netPts = net.map((v, i) => `${(i * slot + slot / 2).toFixed(2)},${yPx(v).toFixed(1)}`);
  const netArea = `0,${TREND_BASE} ${netPts.join(' ')} ${n * slot},${TREND_BASE}`;
  const hoverRow = hover != null ? rows[hover] : null;
  const hoverLeft = hover != null ? Math.min(84, Math.max(16, ((hover + 0.5) / n) * 100)) : 0;

  const totalGross = gross.reduce((a, b) => a + b, 0);
  const totalNet = net.reduce((a, b) => a + b, 0);
  const avg = totalGross / n;
  let peak = -1;
  gross.forEach((v, i) => { if (v > gross[peak] || peak < 0) peak = i; });

  return (
    <div>
      <div className="trend" onMouseLeave={() => setHover(null)}>
        {/* Y 轴刻度（左水槽） */}
        <div className="trend-y" aria-hidden="true">
          {ticks.map((t) => (
            <span key={t} style={{ top: yPx(t) }}>{fmtShort(t)}</span>
          ))}
          <span style={{ top: TREND_BASE }}>0</span>
        </div>

        {/* 绘图区：SVG 负责柱/线/网格，HTML 负责点/标签/悬停 */}
        <div className="trend-plot">
          <svg viewBox={`0 0 100 ${TREND_H}`} preserveAspectRatio="none" width="100%" height={TREND_H} role="img" aria-label="营业额与产值趋势（USD）" style={{ display: 'block' }}>
            <defs>
              <linearGradient id="t-bar" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="var(--primary)" stopOpacity="0.92" />
                <stop offset="100%" stopColor="var(--primary)" stopOpacity="0.55" />
              </linearGradient>
            </defs>
            {ticks.map((t) => (
              <line key={t} x1={0} x2={100} y1={yPx(t)} y2={yPx(t)} stroke="var(--line-soft)" strokeWidth={1} vectorEffect="non-scaling-stroke" />
            ))}
            {mode !== 'net' ? rows.map((m, i) => {
              const v = gross[i];
              const h = Math.max(0, TREND_BASE - yPx(v));
              const x = i * slot + (slot - bw) / 2;
              return h > 0
                ? <rect key={m?.month ?? i} x={x} y={yPx(v)} width={bw} height={h} rx={0.8} fill="url(#t-bar)" className="t-bar" />
                : null;
            }) : null}
            {mode !== 'gross' && n >= 2 ? (
              <>
                <polygon points={netArea} fill="var(--accent)" opacity={0.08} />
                <polyline points={netPts.join(' ')} fill="none" stroke="var(--accent)" strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" className="t-line" />
              </>
            ) : null}
            <line x1={0} x2={100} y1={TREND_BASE} y2={TREND_BASE} stroke="var(--line)" strokeWidth={1} vectorEffect="non-scaling-stroke" />
          </svg>

          {/* 悬停列高亮 */}
          {hover != null ? <div className="t-hl" style={{ left: `${hover * slot}%`, width: `${slot}%` }} /> : null}

          {/* 净额数据点 */}
          {mode !== 'gross' ? net.map((v, i) => (
            <span key={`d-${i}`} className={`t-dot${hover === i ? ' on' : ''}`} style={{ left: `${(i + 0.5) * slot}%`, top: yPx(v) }} />
          )) : null}

          {/* 柱顶数值（总额） */}
          {mode !== 'net' && showBarLabels ? gross.map((v, i) => v > 0 ? (
            <span key={`v-${i}`} className={`t-vlab${hover === i ? ' on' : ''}`} style={{ left: `${(i + 0.5) * slot}%`, top: yPx(v) - 5 }}>{fmtShort(v)}</span>
          ) : null) : null}

          {/* 点上数值（仅净额模式，避免双序列拥挤） */}
          {mode === 'net' && showBarLabels ? net.map((v, i) => v > 0 ? (
            <span key={`n-${i}`} className={`t-vlab accent${hover === i ? ' on' : ''}`} style={{ left: `${(i + 0.5) * slot}%`, top: yPx(v) - 5 }}>{fmtShort(v)}</span>
          ) : null) : null}

          {/* 每月命中区（悬停） */}
          {rows.map((m, i) => (
            <div key={`h-${m?.month ?? i}`} className="t-hit" style={{ left: `${i * slot}%`, width: `${slot}%` }} onMouseEnter={() => setHover(i)} />
          ))}

          {/* 悬浮提示卡 */}
          {hoverRow ? (
            <div className="t-tip" style={{ left: `${hoverLeft}%` }}>
              <b>{hoverRow.month}</b>
              {mode !== 'net' ? <div><i className="sw" style={{ background: 'var(--primary)' }} />营业额（含税） <em>{money(Number(hoverRow.total_usd) || 0, 'USD')}</em></div> : null}
              {mode !== 'gross' ? <div><i className="sw dot" style={{ background: 'var(--accent)' }} />产值（不含税） <em>{money(Number(hoverRow.net_usd ?? hoverRow.total_usd) || 0, 'USD')}</em></div> : null}
              <div className="sub">税额 {money(Number(hoverRow.tax_total) || 0, 'USD')} · 合同 {num(hoverRow.contracts, 0)} 份</div>
            </div>
          ) : null}
        </div>

        {/* X 轴月份标签 */}
        <div className="trend-x" aria-hidden="true">
          {rows.map((m, i) => (
            <span key={`x-${m?.month ?? i}`} style={{ width: `${slot}%` }} className={i % every === 0 || i === n - 1 ? '' : 'off'}>
              {String(m?.month ?? '').slice(5) || '?'}
            </span>
          ))}
        </div>
      </div>

      {/* 底部汇总条 */}
      <div className="trend-foot">
        <div className="tf"><span>累计营业额</span><b>{money(totalGross, 'USD')}</b></div>
        <div className="tf"><span>累计产值</span><b>{money(totalNet, 'USD')}</b></div>
        <div className="tf"><span>月均营业额</span><b>{money(avg, 'USD')}</b></div>
        <div className="tf"><span>峰值月份</span><b>{rows[peak]?.month ?? '—'}<small>{rows[peak] ? ` · ${fmtShort(gross[peak])}` : ''}</small></b></div>
      </div>
    </div>
  );
}

/* ============ 业务信息：合同 → 投产 → 交付 → 开票 → 收款 五个业务节点累计单量 ============ */
const STAGE_CLASS = ['s0', 's1', 's2', 's3', 's4'];

function Pipeline({ insights }: { insights: Insights }) {
  // 后端按角色把无权模块的数量置空（null），此处跳过这些行
  const stages = (insights.funnel ?? []).filter((s) => s.count != null) as Array<{ key: string; label: string; count: number }>;
  const max = Math.max(...stages.map((s) => s.count), 1);
  return (
    <div className="funnel">
      {stages.map((s, i) => {
        const width = s.count > 0 ? Math.max(2, Math.round((s.count / max) * 100)) : 0;
        return (
          <div className="fn-row" key={s.key}>
            <span className="nm"><i className={STAGE_CLASS[i % STAGE_CLASS.length]} />{s.label}</span>
            <span className="fn-bar">
              <i className={STAGE_CLASS[i % STAGE_CLASS.length]} style={{ width: `${width}%` }} />
            </span>
            <span className="fn-val">
              <b>{num(s.count, 0)}</b>
              <span>单</span>
            </span>
          </div>
        );
      })}
    </div>
  );
}

export function OverviewPage() {
  const navigate = useNavigate();
  const [year, setYear] = useState('');
  const [trendMode, setTrendMode] = useState<TrendMode>('both');

  const dashEndpoint = useMemo(() => `/api/dashboard${year ? `?year=${year}` : ''}`, [year]);
  const { data: raw, loading: dashLoading, error: dashError } = useDetail<Dashboard>(dashEndpoint);
  const { data: insights, loading: insightsLoading } = useDetail<Insights>('/api/insights');

  const compatible = !!(raw && typeof raw === 'object' && (raw as Dashboard).revenue && Array.isArray((raw as Dashboard).monthly));
  const data = compatible ? raw : null;
  const monthly = data?.monthly ?? [];
  const netSeries = monthly.map((m) => Number(m?.net_usd ?? m?.total_usd) || 0);

  const safeMoney = (v: unknown, cur = 'USD') => money(typeof v === 'number' ? v : Number(v) || 0, cur);
  const safeNum = (v: unknown, digits = 0) => num(typeof v === 'number' ? v : Number(v) || 0, digits);

  const updatedText = data?.updated_at ? new Date(data.updated_at).toLocaleString('zh-CN', { hour12: false }) : '';

  // 环比：末月 vs 上一月成交额
  const deltaEl = (() => {
    if (monthly.length < 2) return null;
    const last = Number(monthly[monthly.length - 1]?.total_usd) || 0;
    const prev = Number(monthly[monthly.length - 2]?.total_usd) || 0;
    if (!prev) return <span className="delta flat">新增</span>;
    const pct = Math.round(((last - prev) / prev) * 100);
    return <span className={`delta ${pct >= 0 ? 'up' : 'down'}`}>{pct >= 0 ? '▲' : '▼'} {Math.abs(pct)}%</span>;
  })();

  const receivable = Number(data?.receivable_ars) || 0;
  const pendingReturns = Number(data?.pending_returns) || 0;
  const todoTotal = (insights?.todos ?? []).reduce((a, t) => a + (Number(t.count) || 0), 0);
  // 后端按角色把受限数字/金额置空；此处仅在拿到值时渲染对应 KPI，避免受限岗位看到误导性的 0。
  const showRevenue = data?.revenue?.total != null;
  const showReceivable = data?.receivable_ars != null;
  const has = (v: unknown) => v != null;

  return (
    <div className="board">
      <div className="panel">
        <div className="panel-head">
          <div>
            <h2>经营总览</h2>
            <div className="desc">全部数字来自核心服务 /api/dashboard 与 /api/insights 实时聚合，非演示数据</div>
          </div>
          <div className="spacer" />
          <YearField value={year} onChange={setYear} years={data?.years ?? []} />
        </div>
        {(dashError || (!dashLoading && raw && !compatible)) ? (
          <div className="panel-body" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--gap)' }}>
            {dashError ? <ErrorNotice message={dashError} onRetry={() => setYear((y) => `${y}`)} /> : null}
            {!dashLoading && raw && !compatible ? (
              <div className="notice warn">
                <div>
                  <div style={{ fontWeight: 650, marginBottom: 4 }}>后端契约与前端期望不一致</div>
                  <div>运行中的 core-api 返回的 <span className="num">/api/dashboard</span> 缺少 <span className="num">revenue</span> 或 <span className="num">monthly</span> 字段——通常是磁盘代码更新但服务未重启。请重启 18890 后刷新本页。</div>
                </div>
              </div>
            ) : null}
          </div>
        ) : null}
      </div>

      {data ? (
        <>
          {/* KPI 条 */}
          <div className="grid">
            {showRevenue ? <Kpi lead label="营业额（USD）" value={safeMoney(data.revenue?.total, 'USD')} foot={<>{deltaEl}<span>{safeNum(data.revenue?.contracts, 0)} 份合同 · {data.scope?.year || '全部年度'}</span></>} spark={netSeries} tone="info" /> : null}
            {showRevenue ? <Kpi label="产值（不含税 USD）" value={safeMoney(data.revenue?.net_total, 'USD')} foot={<span>税额 {safeMoney(data.revenue?.tax_total, 'USD')}</span>} /> : null}
            {showReceivable ? <Kpi label="应收账款（ARS）" value={safeMoney(receivable, 'ARS')} alert={receivable > 0} foot={<span>已授权发票未收</span>} /> : null}
            {has(data.customers) ? <Kpi label="活跃客户" value={safeNum(data.customers, 0)} foot={<span>active 客户</span>} /> : null}
            {has(data.active_contracts) ? <Kpi label="进行中合同" value={safeNum(data.active_contracts, 0)} tone="ok" foot={<span>未完成 / 未取消</span>} /> : null}
            {has(data.open_work_orders) ? <Kpi label="未完工生产工单" value={safeNum(data.open_work_orders, 0)} foot={<span>在制工单</span>} /> : null}
            {has(data.pending_returns) ? <Kpi label="待处理退货" value={safeNum(pendingReturns, 0)} alert={pendingReturns > 0} foot={<span>未结案退货</span>} /> : null}
          </div>

          {/* 三栏：趋势图（左 8） + 洞察侧栏（右 4） */}
          <div className="grid">
            <div className="c8">
              <div className="panel">
                <div className="panel-head">
                  <div>
                    <h2>营业额与产值趋势（USD）</h2>
                    <div className="desc">数据更新：{updatedText || '—'}</div>
                  </div>
                  <div className="spacer" />
                  <div className="chart-tools">
                    <div className="chart-legend">
                      <span className={`lg${trendMode === 'net' ? ' off' : ''}`}><i style={{ background: 'var(--primary)' }} />营业额（含税）</span>
                      <span className={`lg${trendMode === 'gross' ? ' off' : ''}`}><i className="lg-dot" style={{ background: 'var(--accent)' }} />产值（不含税）</span>
                    </div>
                    <div className="seg" role="tablist" aria-label="图表序列">
                      <button type="button" className={trendMode === 'both' ? 'on' : ''} onClick={() => setTrendMode('both')}>双序列</button>
                      <button type="button" className={trendMode === 'gross' ? 'on' : ''} onClick={() => setTrendMode('gross')}>仅营业额</button>
                      <button type="button" className={trendMode === 'net' ? 'on' : ''} onClick={() => setTrendMode('net')}>仅产值</button>
                    </div>
                  </div>
                </div>
                <div className="panel-body">
                  {showRevenue ? <TrendChart data={monthly} mode={trendMode} /> : <div className="muted" style={{ padding: '18px 4px' }}>当前角色无权查看成交金额数据。</div>}
                </div>
              </div>
            </div>

            <div className="c4" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--gap)' }}>
              <div className="panel">
                <div className="panel-head">
                  <div>
                    <h2>业务信息</h2>
                    <div className="desc">合同 → 投产 → 交付 → 开票 → 收款 累计单量</div>
                  </div>
                </div>
                <div className="panel-body">
                  {insights ? (
                    <Pipeline insights={insights} />
                  ) : insightsLoading ? <Spinner label="读取业务数据…" /> : <div className="stack-empty">暂无业务数据。</div>}
                </div>
              </div>
            </div>
          </div>

          {/* 待办 + 预警 */}
          <div className="grid">
            <div className="c7">
              <div className="panel">
                <div className="panel-head">
                  <div>
                    <h2>我的待办</h2>
                    <div className="desc">按当前状态实时汇总，点击进入对应模块</div>
                  </div>
                  <div className="spacer" />
                  <span className={`head-chip${todoTotal > 0 ? ' hot' : ''}`}>待处理 {num(todoTotal, 0)}</span>
                </div>
                {insights ? (
                  <div className="stack">
                    {insights.todos.map((t) => (
                      <div className="stack-item" key={t.key}>
                        <button type="button" className="todo-row" onClick={() => navigate(t.route)}>
                          <span className="tk-dot" style={{ background: TONE_COLOR[t.tone] }} />
                          <span className="nm">{t.label}</span>
                          <span className={`tc${t.count > 0 ? ` ${t.tone}` : ''}`}>{num(t.count, 0)}</span>
                          <span className="chev" aria-hidden="true">›</span>
                        </button>
                      </div>
                    ))}
                  </div>
                ) : <div className="panel-body">{insightsLoading ? <Spinner label="读取待办…" /> : <div className="stack-empty">暂无待办数据。</div>}</div>}
              </div>
            </div>

            <div className="c5">
              <div className="panel">
                <div className="panel-head">
                  <div>
                    <h2>预警与提醒</h2>
                    <div className="desc">逾期 / 应收 / 退货 / 草稿 / 报价到期</div>
                  </div>
                </div>
                {insights ? (
                  insights.alerts.length ? (
                    <div>
                      {insights.alerts.map((a) => (
                        <div className="notif-item" key={a.key} role="button" tabIndex={0} onClick={() => navigate(a.route)} onKeyDown={(e) => { if (e.key === 'Enter') navigate(a.route); }}>
                          <span className="ndot" style={{ background: TONE_COLOR[a.tone] }} />
                          <div className="nb"><b>{a.title}</b><p>{a.body}</p></div>
                          <span className="chev" aria-hidden="true">›</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="all-ok">
                      <span className="ok-ring" aria-hidden="true">✓</span>
                      当前无预警，一切正常。
                    </div>
                  )
                ) : <div className="panel-body">{insightsLoading ? <Spinner label="读取预警…" /> : null}</div>}
              </div>
            </div>
          </div>
        </>
      ) : dashLoading ? (
        <div className="panel"><div className="panel-body"><Spinner label="正在读取经营数据…" /></div></div>
      ) : !dashError ? (
        <EmptyState>暂无看板数据。</EmptyState>
      ) : null}
    </div>
  );
}
